import React, { useState } from 'react';
import './Alerts.css';

const sampleNotifications = [
  {
    id: 1,
    severity: 'high',
    title: 'Emergency Stop Triggered',
    device: 'Elevator 3 — Building A',
    description: 'Emergency stop button was activated on floor 7. Elevator has been halted and requires manual inspection before resuming service.',
    timestamp: '2025-03-26 10:30 AM',
    read: false,
  },
  {
    id: 2,
    severity: 'high',
    title: 'Door Obstruction Detected',
    device: 'Elevator 1 — Building C',
    description: 'Repeated door obstruction events detected over the past 15 minutes. Door sensor may require recalibration or replacement.',
    timestamp: '2025-03-26 10:12 AM',
    read: false,
  },
  {
    id: 3,
    severity: 'medium',
    title: 'Unusual Vibration Pattern',
    device: 'Elevator 2 — Building A',
    description: 'Vibration levels exceeded normal operating range during upward travel between floors 4–8. Scheduled maintenance recommended.',
    timestamp: '2025-03-26 09:45 AM',
    read: false,
  },
  {
    id: 4,
    severity: 'medium',
    title: 'Drive Temperature Warning',
    device: 'Elevator 4 — Building B',
    description: 'Motor drive temperature has risen to 78°C, approaching the 85°C warning threshold. Monitor closely during peak usage.',
    timestamp: '2025-03-26 09:20 AM',
    read: true,
  },
  {
    id: 5,
    severity: 'low',
    title: 'Scheduled Maintenance Due',
    device: 'Elevator 1 — Building A',
    description: 'Routine maintenance interval of 90 days has been reached. Schedule inspection for brake pads, door alignment, and rope tension.',
    timestamp: '2025-03-26 08:00 AM',
    read: true,
  },
  {
    id: 6,
    severity: 'low',
    title: 'Firmware Update Available',
    device: 'Elevator 2 — Building C',
    description: 'Controller firmware v3.2.1 is available. Current version: v3.1.4. Update includes improved floor leveling accuracy.',
    timestamp: '2025-03-25 04:15 PM',
    read: true,
  },
  {
    id: 7,
    severity: 'medium',
    title: 'Overload Event Logged',
    device: 'Elevator 3 — Building B',
    description: 'Elevator was overloaded by approximately 120 kg at lobby level during morning rush. Doors remained open until load was reduced.',
    timestamp: '2025-03-25 09:05 AM',
    read: true,
  },
  {
    id: 8,
    severity: 'high',
    title: 'Communication Loss',
    device: 'Elevator 2 — Building B',
    description: 'Lost communication with elevator controller for 45 seconds. Connection restored automatically. Investigate network infrastructure.',
    timestamp: '2025-03-25 07:30 AM',
    read: true,
  },
];

const severityConfig = {
  high: { label: 'Critical', color: 'text-red-400', bg: 'bg-red-500/20', border: 'border-red-500/60', icon: '🔴' },
  medium: { label: 'Warning', color: 'text-amber-400', bg: 'bg-amber-500/20', border: 'border-amber-500/60', icon: '🟡' },
  low: { label: 'Info', color: 'text-blue-400', bg: 'bg-blue-500/20', border: 'border-blue-500/60', icon: '🔵' },
};

const Alerts = () => {
  const [filter, setFilter] = useState('all');
  const [notifications, setNotifications] = useState(sampleNotifications);

  const filtered = filter === 'all'
    ? notifications
    : notifications.filter((n) => n.severity === filter);

  const unreadCount = notifications.filter((n) => !n.read).length;
  const countBySeverity = (sev) => notifications.filter((n) => n.severity === sev).length;

  const handleMarkRead = (id) => {
    setNotifications((prev) =>
      prev.map((n) => (n.id === id ? { ...n, read: true } : n))
    );
  };

  const handleMarkAllRead = () => {
    setNotifications((prev) => prev.map((n) => ({ ...n, read: true })));
  };

  return (
    <div className="bg-[#0F172A] p-6 overflow-y-auto h-full w-full flex flex-col">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-bold text-white">Alerts & Notifications</h1>
          {unreadCount > 0 && (
            <span className="px-2.5 py-0.5 text-xs font-semibold rounded-full bg-red-500/20 text-red-400 border border-red-500/40">
              {unreadCount} new
            </span>
          )}
        </div>
        {unreadCount > 0 && (
          <button
            onClick={handleMarkAllRead}
            className="px-4 py-2 text-sm font-medium text-cyan-300 bg-cyan-500/10 border border-cyan-500/30 rounded-lg hover:bg-cyan-500/20 transition-colors"
          >
            Mark all as read
          </button>
        )}
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-4 gap-4 mb-6">
        <div className="bg-slate-800/60 border border-slate-700/50 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-white">{notifications.length}</p>
          <p className="text-xs text-slate-400 font-medium uppercase tracking-wider mt-1">Total</p>
        </div>
        <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-red-400">{countBySeverity('high')}</p>
          <p className="text-xs text-red-400/80 font-medium uppercase tracking-wider mt-1">Critical</p>
        </div>
        <div className="bg-amber-500/10 border border-amber-500/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-amber-400">{countBySeverity('medium')}</p>
          <p className="text-xs text-amber-400/80 font-medium uppercase tracking-wider mt-1">Warning</p>
        </div>
        <div className="bg-blue-500/10 border border-blue-500/30 rounded-xl p-4 text-center">
          <p className="text-2xl font-bold text-blue-400">{countBySeverity('low')}</p>
          <p className="text-xs text-blue-400/80 font-medium uppercase tracking-wider mt-1">Info</p>
        </div>
      </div>

      {/* Filter Bar */}
      <div className="flex gap-2 mb-6">
        {['all', 'high', 'medium', 'low'].map((f) => (
          <button
            key={f}
            onClick={() => setFilter(f)}
            className={`px-4 py-2 text-sm font-medium rounded-lg border transition-colors ${
              filter === f
                ? 'bg-cyan-500/20 text-cyan-300 border-cyan-500/40'
                : 'bg-slate-800/60 text-slate-400 border-slate-700/50 hover:text-white hover:border-slate-600'
            }`}
          >
            {f === 'all' ? 'All' : severityConfig[f].label}
          </button>
        ))}
      </div>

      {/* Notifications List */}
      <div className="space-y-3 flex-1">
        {filtered.length === 0 ? (
          <div className="text-center py-16 text-slate-500">
            <p className="text-4xl mb-3">🔔</p>
            <p className="text-lg font-medium">No alerts in this category</p>
          </div>
        ) : (
          filtered.map((notification) => {
            const config = severityConfig[notification.severity];
            return (
              <div
                key={notification.id}
                onClick={() => handleMarkRead(notification.id)}
                className={`relative bg-slate-800/60 border rounded-xl p-5 cursor-pointer transition-all hover:bg-slate-700/40 ${
                  !notification.read ? 'border-l-4 ' + config.border : 'border-slate-700/50'
                }`}
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="text-sm">{config.icon}</span>
                      <span className={`text-xs font-semibold uppercase tracking-wider ${config.color}`}>
                        {config.label}
                      </span>
                      {!notification.read && (
                        <span className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse"></span>
                      )}
                    </div>
                    <h3 className={`text-base font-semibold mb-1 ${!notification.read ? 'text-white' : 'text-slate-300'}`}>
                      {notification.title}
                    </h3>
                    <p className="text-xs font-medium text-cyan-400/80 mb-2">{notification.device}</p>
                    <p className="text-sm text-slate-400 leading-relaxed">{notification.description}</p>
                  </div>
                  <span className="text-xs text-slate-500 whitespace-nowrap flex-shrink-0 mt-1">
                    {notification.timestamp}
                  </span>
                </div>
              </div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default Alerts;
